import { api } from "./client"
import type {
  Decision,
  GhlContactRich,
  GhlConversationRich,
  QueueResponse,
  GhlAnalytics,
  GhlAppointment,
  GhlContact,
  GhlContactDetail,
  GhlContactRow,
  GhlConversationRow,
  GhlDnd,
  GhlMessage,
  GhlMessageType,
  GhlNote,
  GhlOpportunity,
  GhlOpportunityRow,
  GhlOpportunityStatus,
  GhlPipeline,
  GhlTask,
  GhlTaskRow,
  GhlUser,
} from "./ghl-types"

// MOCK-ONLY API — /v1/ghl/team/:teamId/*. Each call is 1:1 with a GHL v2
// read/write; `analytics` is what the real backend derives from stored
// webhooks (GHL has no reporting API). Real BE: ❌ no counterpart yet.

export interface ContactFilters { q?: string; tag?: string; owner?: string; stage?: string; consent?: "opted_out" | "ok"; followUp?: string }

const base = (teamId: number | string) => `/ghl/team/${teamId}`

export const ghlApi = {
  users: (teamId: number | string) => api.get<GhlUser[]>(`${base(teamId)}/users`),

  contacts: (teamId: number | string, f: ContactFilters = {}) =>
    api.get<GhlContactRow[]>(`${base(teamId)}/contacts`, { q: f.q, tag: f.tag, owner: f.owner, stage: f.stage, consent: f.consent }),
  contact: (teamId: number | string, contactId: string) => api.get<GhlContactDetail>(`${base(teamId)}/contacts/${contactId}`),
  patchContact: (teamId: number | string, contactId: string, patch: { tags?: string[]; assignedTo?: string | null; dndSettings?: GhlDnd }) =>
    api.patch<GhlContact>(`${base(teamId)}/contacts/${contactId}`, patch),
  bulkContacts: (teamId: number | string, body: { ids: string[]; addTag?: string; removeTag?: string; assignedTo?: string | null }) =>
    api.post<{ updated: number }>(`${base(teamId)}/contacts/bulk`, body),

  conversations: (teamId: number | string, f: { unread?: boolean; assignedTo?: string; starred?: boolean } = {}) =>
    api.get<GhlConversationRow[]>(`${base(teamId)}/conversations`, { unread: f.unread, assignedTo: f.assignedTo, starred: f.starred }),
  messages: (teamId: number | string, conversationId: string) =>
    api.get<GhlMessage[]>(`${base(teamId)}/conversations/${conversationId}/messages`),
  sendMessage: (teamId: number | string, conversationId: string, body: { type: GhlMessageType; body: string; subject?: string | null }) =>
    api.post<GhlMessage>(`${base(teamId)}/conversations/${conversationId}/messages`, body),
  markRead: (teamId: number | string, conversationId: string) => api.post<{ ok: true }>(`${base(teamId)}/conversations/${conversationId}/read`, {}),

  pipelines: (teamId: number | string) => api.get<GhlPipeline[]>(`${base(teamId)}/pipelines`),
  opportunities: (teamId: number | string, f: { status?: GhlOpportunityStatus; stage?: string; assignedTo?: string } = {}) =>
    api.get<GhlOpportunityRow[]>(`${base(teamId)}/opportunities`, { status: f.status, stage: f.stage, assignedTo: f.assignedTo }),
  patchOpportunity: (teamId: number | string, id: string, patch: { pipelineStageId?: string; status?: GhlOpportunityStatus; monetaryValue?: number; assignedTo?: string | null }) =>
    api.patch<GhlOpportunity>(`${base(teamId)}/opportunities/${id}`, patch),

  tasks: (teamId: number | string, f: { assignedTo?: string; completed?: boolean } = {}) =>
    api.get<GhlTaskRow[]>(`${base(teamId)}/tasks`, { assignedTo: f.assignedTo, completed: f.completed }),
  createTask: (teamId: number | string, body: { contactId: string; title: string; body?: string | null; dueDate: string; assignedTo?: string | null }) =>
    api.post<GhlTask>(`${base(teamId)}/tasks`, body),
  patchTask: (teamId: number | string, id: string, patch: { completed?: boolean; title?: string; dueDate?: string; assignedTo?: string | null }) =>
    api.patch<GhlTask>(`${base(teamId)}/tasks/${id}`, patch),

  notes: (teamId: number | string, contactId: string) => api.get<GhlNote[]>(`${base(teamId)}/notes`, { contactId }),
  createNote: (teamId: number | string, body: { contactId: string; body: string }) => api.post<GhlNote>(`${base(teamId)}/notes`, body),

  appointments: (teamId: number | string) => api.get<GhlAppointment[]>(`${base(teamId)}/appointments`),

  // The work
  contactsRich: (teamId: number | string, f: ContactFilters = {}) =>
    api.get<GhlContactRich[]>(`${base(teamId)}/contacts-rich`, { q: f.q, tag: f.tag, owner: f.owner, stage: f.stage, consent: f.consent, followUp: f.followUp }),
  conversationsRich: (teamId: number | string, f: { unread?: boolean; assignedTo?: string; starred?: boolean } = {}) =>
    api.get<GhlConversationRich[]>(`${base(teamId)}/conversations-rich`, { unread: f.unread, assignedTo: f.assignedTo, starred: f.starred }),
  queue: (teamId: number | string) => api.get<QueueResponse>(`${base(teamId)}/queue`),
  decide: (teamId: number | string, d: Decision) => api.post<{ ok: true; note?: string }>(`${base(teamId)}/decide`, d),
  bulkMessage: (teamId: number | string, body: { contactIds: string[]; body: string }) =>
    api.post<{ sent: number; skippedConsent: number; skippedNoContact: number }>(`${base(teamId)}/bulk-message`, body),
  analytics: (teamId: number | string, days: number) => api.get<GhlAnalytics>(`${base(teamId)}/analytics`, { days }),
}
